import { CancelSurveyReasonCode, WebActionEvents } from '@griply/types';
import { ChurnSurveyContext } from '@griply/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SurveyAnswers,
  SurveyDetailSelection,
  useSurveySteps,
} from 'shared/hooks/use-survey-steps';
import { useTrackEvents } from 'shared/hooks/use-track-events';

const BASE = 'delete-account-survey';

export type UseDeleteAccountSurveyOptions = {
  // Submitting and skipping both delete the account (with or without answers);
  // backing out and dismissing cancel it. The survey never blocks deletion, it
  // only decides whether we learn why.
  onDelete: () => void;
  onCancel: () => void;
};

export const useDeleteAccountSurvey = ({
  onDelete,
  onCancel,
}: UseDeleteAccountSurveyOptions) => {
  const { t } = useTranslation();
  const track = useTrackEvents();

  const onStarted = useCallback(
    () => track(WebActionEvents.DeleteAccountSurveyStarted, undefined),
    [track],
  );

  const onReasonSelected = useCallback(
    (reason: CancelSurveyReasonCode) =>
      track(WebActionEvents.DeleteAccountSurveyReasonSelected, {
        reason_code: reason,
      }),
    [track],
  );

  const onDetailSelected = useCallback(
    (selection: SurveyDetailSelection) =>
      track(WebActionEvents.DeleteAccountSurveyDetailSelected, selection),
    [track],
  );

  const onSubmit = useCallback(
    (answers: SurveyAnswers) => {
      track(WebActionEvents.DeleteAccountSurveyCompleted, answers);
      onDelete();
    },
    [onDelete, track],
  );

  const survey = useSurveySteps({
    context: ChurnSurveyContext.DeleteAccount,
    submitLabel: t(`${BASE}.submit`),
    reasonSubtitle: t(`${BASE}.reason.subtitle`),
    onStarted,
    onReasonSelected,
    onDetailSelected,
    onSubmit,
    onExit: onCancel,
  });

  // The reason step's skip button deletes without answers; deeper steps step
  // back through the survey without ending it.
  return {
    ...survey,
    onDismiss: onCancel,
    skipLabel: t(`${BASE}.skip`),
    onSkip: onDelete,
  };
};
